import api from 'services/api';
import { array, enforce, nullable, num, numeric, object, runSchema, str } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { filterConfigSchema, listColumnConfigSchema, type FilterConfig, type ListColumnConfig } from 'api/shared/listConfig';

// GET /locks — the list read. Backend: routes/locks.js `GET /locks` →
// `getLocks` (modules/generic_functions/locks/getLocks.js) for the paginated
// rows, then the route enriches each row with `folder_path` (buildFolderPath)
// and wraps them in `{ locks, allowedZones, config, filtersConfig, pagination }`.
//
// `LockRow` is a SEPARATE type from the single-GET `Lock`, even though the
// scalar columns match: the two come from different code paths (the list route
// adds `folder_path`; the single-GET does not) — equal-by-coincidence, not
// identity (see ../../README.md).
export interface LockRow {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly folder_id: number | null;
  readonly zone_name: string;
  readonly limit: number;
  readonly limit_allocation: number;
  readonly created_at: string;
  readonly updated_at: string;
  // Enriched per-row by the list route (null when the lock has no folder).
  readonly folder_path: FolderPath | null;
}

interface Pagination {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

export interface LocksPage {
  readonly locks: readonly LockRow[];
  readonly pagination: Pagination;
  // Zone names the user may list, for the zone filter dropdown.
  readonly allowedZones: readonly string[];
  // List-table config, shared across list endpoints (see api/shared/listConfig).
  readonly config: readonly ListColumnConfig[];
  readonly filtersConfig: readonly FilterConfig[];
}

const rowSchema = object<LockRow>({
  id: num(),
  name: str(),
  description: nullable(str()),
  folder_id: nullable(num()),
  zone_name: str(),
  limit: num(),
  limit_allocation: num(),
  created_at: str(),
  updated_at: str(),
  folder_path: nullable(folderPathSchema),
});

// COUNT-derived totals can arrive as numeric strings from Postgres; `numeric()`
// normalizes both number and string.
const paginationSchema = object<Pagination>({
  total_records: numeric(),
  total_pages: numeric(),
  current_page: numeric(),
  limit: numeric(),
  offset: numeric(),
});

const pageSchema = object<LocksPage>({
  locks: array(rowSchema),
  pagination: paginationSchema,
  allowedZones: array(str()),
  config: array(listColumnConfigSchema),
  filtersConfig: array(filterConfigSchema),
});

/**
 * GET `/locks`. Returns the full list page (rows + pagination + list config).
 * Throws on transport error or structural validation failure of the response.
 */
export const getLocks = async (params: Record<string, unknown> = {}): Promise<LocksPage> => {
  const res = await api.get('/locks', { params });
  const r = runSchema(pageSchema, res.data, 'getLocks');
  if (!r.ok) {
    throw new Error('getLocks: response failed validation');
  }
  return enforce(r.value, r.warnings, ['locks[*].id']);
};
